import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, BadgeCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useDevAuth } from "@/context/DevAuthContext";
import { PageHero } from "@/components/layout/PageHero";
import { StatusBadge } from "@/components/dev/BillingCard";

// Stripe success return: poll the checkout session until Stripe confirms the payment.
export default function DevPaymentReturn() {
  const { t, fmtDate } = useI18n();
  const { refresh } = useDevAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/dev/billing/checkout/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", billing: data.billing });
          refresh();
          return;
        }
        if (data.status === "expired") return setState({ phase: "failed" });
      } catch {
        if (tries > 3) return setState({ phase: "failed" });
      }
      if (tries < 20) timer = setTimeout(poll, 2000);
      else setState({ phase: "failed" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <div data-testid="payment-return-page">
      <PageHero eyebrow={t("dev.eyebrow")} title={t("billing.title")} />
      <section className="mx-auto max-w-xl px-4 sm:px-0">
        <div className="card-carbon space-y-4 rounded-3xl p-6 sm:p-8">
          {state.phase === "checking" && <p className="flex items-center gap-2 text-neutral-300" data-testid="payment-checking"><Loader2 className="h-5 w-5 animate-spin text-[var(--gold)]" /> {t("billing.checking")}</p>}
          {state.phase === "paid" && (
            <div className="space-y-3" data-testid="payment-success">
              <p className="flex items-center gap-2 font-semibold text-emerald-200"><BadgeCheck className="h-5 w-5" /> {t("billing.success")}</p>
              <StatusBadge status={state.billing.status} testId="payment-status-badge" />
              {state.billing.period_end && <p className="text-sm text-neutral-300" data-testid="payment-period-end">{t("billing.paidUntil", { date: fmtDate(state.billing.period_end) })}</p>}
            </div>
          )}
          {state.phase === "failed" && <p className="flex items-center gap-2 text-red-200" role="alert" data-testid="payment-failed"><AlertTriangle className="h-5 w-5" /> {t("billing.cancelled")}</p>}
          <Link to="/espace-developpeur" className="btn-gold btn-sm" data-testid="payment-back-dashboard">{t("dev.dashboard")}</Link>
        </div>
      </section>
    </div>
  );
}
